/**
 * i18n Utilities
 * 
 * Translation utilities with fallback support
 */

import { FALLBACK_LANGUAGE } from './config';

// Static imports for all translation files (required for create-react-app)
import enTranslations from './locales/en.json';
import zhTranslations from './locales/zh.json';
import hiTranslations from './locales/hi.json';
import esTranslations from './locales/es.json';
import arTranslations from './locales/ar.json';
import frTranslations from './locales/fr.json';
import bnTranslations from './locales/bn.json';
import ptTranslations from './locales/pt.json';
import ruTranslations from './locales/ru.json';
import urTranslations from './locales/ur.json';
import idTranslations from './locales/id.json';
import jaTranslations from './locales/ja.json';
import deTranslations from './locales/de.json';
import swTranslations from './locales/sw.json';
import trTranslations from './locales/tr.json';
import viTranslations from './locales/vi.json';
import koTranslations from './locales/ko.json';
import itTranslations from './locales/it.json';
import tlTranslations from './locales/tl.json';
import faTranslations from './locales/fa.json';
import miTranslations from './locales/mi.json';
import smTranslations from './locales/sm.json';
import toTranslations from './locales/to.json';

// Translation map for quick lookup
const translationMap: Record<string, Translations> = {
  en: enTranslations,
  zh: zhTranslations,
  hi: hiTranslations,
  es: esTranslations,
  ar: arTranslations,
  fr: frTranslations,
  bn: bnTranslations,
  pt: ptTranslations,
  ru: ruTranslations,
  ur: urTranslations,
  id: idTranslations,
  ja: jaTranslations,
  de: deTranslations,
  sw: swTranslations,
  tr: trTranslations,
  vi: viTranslations,
  ko: koTranslations,
  it: itTranslations,
  tl: tlTranslations,
  fa: faTranslations,
  mi: miTranslations,
  sm: smTranslations,
  to: toTranslations,
};

// Translation type - nested object structure
export type Translations = {
  [key: string]: string | Translations;
};

// Flattened translations for quick lookup
type FlatTranslations = Record<string, string>;

/**
 * Flatten nested translation object
 * Example: { auth: { login: "Log in" } } => { "auth.login": "Log in" }
 */
export const flattenTranslations = (
  obj: Translations,
  prefix = ''
): FlatTranslations => {
  const result: FlatTranslations = {};
  
  for (const key in obj) {
    const value = obj[key];
    const newKey = prefix ? `${prefix}.${key}` : key;
    
    if (typeof value === 'string') {
      result[newKey] = value;
    } else {
      Object.assign(result, flattenTranslations(value, newKey));
    }
  }
  
  return result;
};

/**
 * Get translation for a key with fallback support
 */
export const getTranslation = (
  translations: FlatTranslations,
  fallbackTranslations: FlatTranslations,
  key: string,
  params?: Record<string, string | number>
): string => {
  // Try to get translation from current language
  let translation = translations[key];
  
  // Fallback to English if not found
  if (!translation) {
    translation = fallbackTranslations[key];
  }
  
  // If still not found, return the key itself
  if (!translation) {
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Translation missing for key: ${key}`);
    }
    return key;
  }
  
  // Replace parameters if provided
  if (params) {
    return replaceParams(translation, params);
  }
  
  return translation;
};

/**
 * Replace parameters in translation string
 * Supports both {{key}} and {key} formats
 * Example: "Hello {{name}}" with { name: "John" } => "Hello John"
 */
export const replaceParams = (
  text: string,
  params: Record<string, string | number>
): string => {
  // First replace {{key}} format (double braces) - order matters to avoid partial matches
  let result = text.replace(/\{\{(\w+)\}\}/g, (match, key) => {
    return params[key] !== undefined ? String(params[key]) : match;
  });
  // Then replace {key} format (single braces)
  result = result.replace(/\{(\w+)\}/g, (match, key) => {
    return params[key] !== undefined ? String(params[key]) : match;
  });
  return result;
};

/**
 * Translation manager class
 */
export class TranslationManager {
  private currentTranslations: FlatTranslations = {};
  private fallbackTranslations: FlatTranslations = {};
  private currentLanguage: string = FALLBACK_LANGUAGE;
  private isInitialized: boolean = false;
  
  constructor() {
    // Load fallback translations immediately so t() works before loadLanguage
    this.loadFallback();
    // Set current translations to fallback by default
    this.currentTranslations = this.fallbackTranslations;
    this.isInitialized = true;
  }
  
  /**
   * Initialize the translation manager
   */
  async initialize(): Promise<void> {
    if (this.isInitialized) return;
    this.loadFallback();
    this.isInitialized = true;
  }
  
  /**
   * Load fallback (English) translations
   */
  private loadFallback(): void {
    try {
      const fallback = translationMap[FALLBACK_LANGUAGE] || translationMap['en'];
      this.fallbackTranslations = flattenTranslations(fallback);
      
      // Debug: Check if wizard keys are loaded
      if (process.env.NODE_ENV === 'development') {
        const testKey = 'admin.invoices.wizard.createInvoice';
        if (this.fallbackTranslations[testKey]) {
          console.log(`[I18n] ✓ Wizard translations loaded: ${testKey} = "${this.fallbackTranslations[testKey]}"`);
        } else {
          console.warn(`[I18n] ✗ Wizard translations NOT found: ${testKey}`);
          console.log('[I18n] Available admin.invoices keys:', Object.keys(this.fallbackTranslations).filter(k => k.startsWith('admin.invoices')).slice(0, 10));
        }
      }
    } catch (error) {
      console.error('Failed to load fallback translations:', error);
    }
  }
  
  /**
   * Load translations for a specific language
   */
  async loadLanguage(languageCode: string): Promise<void> {
    // Ensure initialized
    await this.initialize();
    
    try {
      const translations = translationMap[languageCode];
      if (!translations) {
        console.warn(`Translations not found for language: ${languageCode}, falling back to English`);
        this.currentTranslations = this.fallbackTranslations;
        this.currentLanguage = FALLBACK_LANGUAGE;
        return;
      }
      this.currentTranslations = flattenTranslations(translations);
      this.currentLanguage = languageCode;
      
      // Debug: Verify wizard translations are loaded
      if (process.env.NODE_ENV === 'development') {
        const testKey = 'admin.invoices.wizard.createInvoice';
        if (this.currentTranslations[testKey]) {
          console.log(`[I18n] ✓ Current language translations loaded: ${testKey} = "${this.currentTranslations[testKey]}"`);
        }
      }
    } catch (error) {
      console.error(`Failed to load translations for language: ${languageCode}`, error);
      // Fallback to English if load fails
      this.currentTranslations = this.fallbackTranslations;
      this.currentLanguage = FALLBACK_LANGUAGE;
    }
  }
  
  /**
   * Get translation for a key
   */
  t(key: string, params?: Record<string, string | number>): string {
    return getTranslation(
      this.currentTranslations,
      this.fallbackTranslations,
      key,
      params
    );
  }
  
  /**
   * Get current language code
   */
  getLanguage(): string {
    return this.currentLanguage;
  }
}

export default TranslationManager;

